import { useState, useEffect } from 'react';

// Map of custom media queries to their actual CSS media query values
// This should match the definitions in app/styles/media.css
const CUSTOM_MEDIA_MAP = {
  '--screen-md': '(max-width: 834px)',
  '--screen-lg': '(min-width: 835px)',
} as const;

type CustomMediaQuery = keyof typeof CUSTOM_MEDIA_MAP;

export function useMediaQuery(query: CustomMediaQuery | (string & {})) {
  const [matches, setMatches] = useState<boolean>(false);

  useEffect(() => {
    // Check if window is available (for SSR compatibility)
    if (typeof window === 'undefined') {
      return;
    }

    // Resolve custom media query to actual CSS media query
    const resolvedQuery = CUSTOM_MEDIA_MAP[query as CustomMediaQuery] ?? query;

    const mediaQuery = window.matchMedia(resolvedQuery);

    // Set initial value
    setMatches(mediaQuery.matches);

    // Create event listener function
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Add event listener
    mediaQuery.addEventListener('change', handleChange);

    // Cleanup function to remove event listener
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
}
